import { resumeHeading, resumeSentence } from '@/lib/session-labels';
import type { SessionStatus } from '@matter_extractor/shared';
import { Button } from '@pms-digital/design-system';
import { SESSION_TEST_IDS } from './contracts';

export interface SessionResumePageProps {
  /** The status of the session THIS browser remembers, asked of the server first (§5.3). */
  readonly status: SessionStatus;
  readonly startedAt: string;
  /** The name of the experience it plays, read off its frozen version. */
  readonly experienceName: string;
  /** « Reprendre » - absent unless the session is still running. */
  readonly onResume: () => void;
  readonly onRestart: () => void;
  readonly restarting: boolean;
  readonly refusal: string | null;
}

/**
 * The reprise page of §5.3 (planche `PageReprendre`).
 *
 * « Une session en cours : une page qui offre Reprendre et Recommencer. Une
 * session terminée ou expirée : une phrase qui le dit, et Recommencer seul. »
 * So the primary button is the one that continues what exists when there is
 * something to continue, and the only button when there is not.
 *
 * The status is the SERVER's, never the browser's memory: what is stored
 * locally is a token and nothing else (`lib/session-memory.ts`), and the page
 * asks « où en est-elle » before drawing anything. That is what makes a session
 * finished in another tab, or expired overnight, show as finished here.
 *
 * The line under the buttons is the mockup's and it is load-bearing:
 * « Recommencer crée une nouvelle session et n'efface pas l'ancienne » (§5.3),
 * and a person who fears losing twenty minutes of answers will not press a
 * button that does not say so.
 */
export function SessionResumePage({
  status,
  startedAt,
  experienceName,
  onResume,
  onRestart,
  restarting,
  refusal,
}: SessionResumePageProps) {
  const running = status === 'in-progress';
  return (
    <section
      data-testid={SESSION_TEST_IDS.resume}
      className="mx-auto flex w-full max-w-3xl flex-col gap-6 pt-4 pb-16"
    >
      <div className="flex flex-col gap-2">
        <h1 className="font-semibold text-h3 text-bleu-nuit sm:text-[28px] sm:leading-9">
          {resumeHeading(status)}
        </h1>
        <p className="max-w-[62ch] text-body text-gray-700">
          {experienceName !== '' && <>« {experienceName} ». </>}
          {resumeSentence(status, startedAt)}
        </p>
      </div>

      {refusal !== null && (
        <p role="alert" className="text-rouge text-small">
          {refusal}
        </p>
      )}

      <div className="flex flex-col items-start gap-3">
        <div className="flex items-center gap-3">
          {running && (
            <Button type="button" variant="secondary" onClick={onResume}>
              Reprendre
            </Button>
          )}
          <Button
            type="button"
            variant={running ? 'accent' : 'secondary'}
            onClick={onRestart}
            disabled={restarting}
          >
            {restarting ? 'Ouverture...' : 'Recommencer'}
          </Button>
        </div>
        <p className="text-gray-600 text-caption">
          Recommencer ouvre une nouvelle conversation ; l’ancienne reste enregistrée.
        </p>
      </div>
    </section>
  );
}
